import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/layout/AdminLayout';
import { IInquiry } from '../types/Inquiry';
import { getInquiries, deleteInquiry as apiDeleteInquiry, toggleReadStatus } from '../services/InquiryService';
import InquiryModal from '../components/inquiries/InquiryModal';
import ConfirmModal from '../components/common/ConfirmModal';
import { useConfirm } from '../hooks/useConfirm';
import styles from '../styles/Dashboard.module.css'; 
import inquiryStyles from '../styles/Inquiries.module.css'; 
import { TrashIcon, EnvelopeIcon, EnvelopeOpenIcon, ChatBubbleBottomCenterTextIcon } from '@heroicons/react/24/outline';
import { TruckIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/solid';
import { useToast } from '../hooks/useToast';

const InquiriesPage: React.FC = () => {
    const [inquiries, setInquiries] = useState<IInquiry[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedInquiry, setSelectedInquiry] = useState<IInquiry | null>(null);
    const toast = useToast();
    const { isOpen, config, confirm, handleConfirm, handleCancel } = useConfirm();

    const fetchInquiries = async () => {
        setLoading(true);
        setError(null);
        try {
            const fetchedInquiries = await getInquiries();
            setInquiries(fetchedInquiries);
        } catch (err: any) {
            setError(err.message || "Fallo al cargar los mensajes desde el servidor.");
            toast.error('Error', 'No se pudieron cargar los mensajes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInquiries();
    }, []);

    const handleDelete = async (id: number, subject: string) => {
        const confirmed = await confirm({
            title: 'Eliminar Consulta',
            message: `¿Estás seguro de eliminar la consulta: "${subject}"? Esta acción no se puede deshacer.`,
            confirmText: 'Eliminar',
            cancelText: 'Cancelar',
            type: 'danger'
        });

        if (confirmed) {
            try {
                const success = await apiDeleteInquiry(id);
                if (success) {
                    fetchInquiries();
                    toast.success('Éxito', `Consulta "${subject}" eliminada con éxito`);
                } else {
                    toast.error('Error', 'Error al eliminar la consulta en el servidor.');
                }
            } catch (err: any) {
                toast.error('Error', `Error al eliminar: ${err.message}`);
            }
        }
    };

    const handleToggleRead = async (id: number, currentState: boolean) => {
        try {
            const newReadStatus = !currentState;
            await toggleReadStatus(id, newReadStatus); 
            fetchInquiries();
            toast.info('Estado actualizado', `Mensaje marcado como ${newReadStatus ? 'leído' : 'no leído'}`);
        } catch (err: any) {
            toast.error('Error', `Error al actualizar estado: ${err.message}`);
        }
    };
    
    const handleViewMessage = async (inquiry: IInquiry) => {
        setSelectedInquiry(inquiry);

        if (inquiry.isRead) {
            return;
        }

        setInquiries(prevInquiries =>
            prevInquiries.map(item =>
                item.id === inquiry.id ? { ...item, isRead: true } : item
            )
        );

        try {
            await toggleReadStatus(inquiry.id, true);
        } catch (err: any) {
            toast.error('Error', 'No se pudo marcar como leído. Reintentando...');
            console.error("Fallo al marcar como leído:", err);
            
            setInquiries(prevInquiries =>
                prevInquiries.map(item =>
                    item.id === inquiry.id ? { ...item, isRead: false } : item
                )
            );
        }
    };

    const handleCloseModal = () => {
        setSelectedInquiry(null);
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
    }
    
    if (loading) {
        return <AdminLayout pageTitle="Bandeja de Mensajes"><p>Cargando mensajes...</p></AdminLayout>;
    }
    
    if (error) { 
        return <AdminLayout pageTitle="Bandeja de Mensajes"><p style={{ color: 'red', textAlign: 'center' }}>Error de Conexión: {error}</p></AdminLayout>;
    }

    return (
        <AdminLayout pageTitle="Bandeja de Mensajes">
            <div className={inquiryStyles.statsBar}>
                <div className={inquiryStyles.statItem}>
                    <span className={inquiryStyles.statValue}>{inquiries.filter(i => !i.isRead).length}</span>
                    <span className={inquiryStyles.statLabel}>Mensajes Sin Leer</span>
                </div>
                <div className={inquiryStyles.statItem}>
                    <span className={inquiryStyles.statValue}>{inquiries.filter(i => i.isQuote).length}</span>
                    <span className={inquiryStyles.statLabel}>Solicitudes de Cotización</span>
                </div>
            </div>

            <div className={styles.recentActivity}>
                <table className={styles.projectTable}>
                    <thead>
                        <tr className={inquiryStyles.headerRow}>
                            <th>Estado</th>
                            <th>Tipo</th>
                            <th>Asunto</th>
                            <th>De</th>
                            <th>Fecha</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {inquiries.map((inquiry) => (
                            <tr 
                                key={inquiry.id} 
                                className={inquiry.isRead ? inquiryStyles.readRow : inquiryStyles.unreadRow}
                                onClick={() => handleViewMessage(inquiry)}
                            >
                                <td>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); handleToggleRead(inquiry.id, inquiry.isRead); }}
                                        title={inquiry.isRead ? "Marcar como No Leído" : "Marcar como Leído"}
                                        className={`${styles.iconButton} ${inquiry.isRead ? inquiryStyles.readIcon : inquiryStyles.unreadIcon}`}
                                    >
                                        {inquiry.isRead ? <EnvelopeOpenIcon className={styles.iconButtonIcon} /> : <EnvelopeIcon className={styles.iconButtonIcon} />}
                                    </button>
                                </td>
                                <td>
                                    <div className={inquiryStyles.typeBadge} title={inquiry.isQuote ? "Solicitud de Cotización" : "Contacto General"}>
                                        {inquiry.isQuote ? <TruckIcon className={inquiryStyles.quoteIcon} /> : <QuestionMarkCircleIcon className={inquiryStyles.genericIcon} />}
                                    </div>
                                </td>
                                <td className={inquiryStyles.subjectCell}>
                                    <div className={inquiryStyles.subjectText} title={inquiry.subject}>{inquiry.subject}</div>
                                    <div className={inquiryStyles.messagePreview}>{inquiry.message.substring(0, 50)}...</div>
                                </td>
                                <td>
                                    <strong>{inquiry.name}</strong> <br />
                                    <small>{inquiry.email}</small>
                                </td>
                                <td>{formatDate(inquiry.receivedAt)}</td>
                                <td>
                                    <div className={styles.actionsCell}>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleViewMessage(inquiry); }} 
                                            title="Ver Mensaje" 
                                            className={inquiryStyles.viewButton}
                                        >
                                            <ChatBubbleBottomCenterTextIcon className={styles.iconButtonIcon} />
                                        </button>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handleDelete(inquiry.id, inquiry.subject); }} 
                                            title="Eliminar" 
                                            className={styles.deleteButton}
                                        >
                                            <TrashIcon className={styles.iconButtonIcon} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {inquiries.length === 0 && <p className={styles.noProjects}>No hay mensajes en la bandeja de entrada.</p>}
            </div>
            
            {/* Modal de Confirmación */}
            <ConfirmModal
                isOpen={isOpen}
                title={config?.title || ''}
                message={config?.message || ''}
                confirmText={config?.confirmText}
                cancelText={config?.cancelText}
                type={config?.type}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
            />

            {/* Modal de Mensaje */}
            {selectedInquiry && (
                <InquiryModal inquiry={selectedInquiry} onClose={handleCloseModal} />
            )}
        </AdminLayout>
    );
};

export default InquiriesPage;